import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CollapsibleTable from '../../components/table';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import ModalSolicitudes from '../../components/ModalSolicitudes/ModalSolicitudes';
import ExportButton from '../../components/BotonEx';
import SyncDialog from '../../components/SyncDialog';
import { TableFilterChips } from '../../components/Table/components/TableFilterChips';
import { useSolicitudesController } from './hooks/useSolicitudesController';
import config from '../../config/config';

// ── Componente aislado para el Popover de totales ──────────────────────────
// Al vivir fuera de Solicitudes, abrir/cerrar el popover NO re-renderiza
// CollapsibleTable ni sus 100+ filas. Solo se actualiza este mini componente.
const TotalesPopover = React.forwardRef(function TotalesPopover({ divisasRef, funnelRef }, ref) {
  const [anchor, setAnchor] = useState(null);
  const [field, setField] = useState(null);

  useImperativeHandle(ref, () => ({
    open: (el, fieldKey) => {
      setAnchor(el);
      setField(fieldKey);
    },
  }), []);

  const divisas = divisasRef.current || {};
  const funnel  = funnelRef.current  || {};
  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={() => { setAnchor(null); setField(null); }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      PaperProps={{
        sx: {
          p: 1.5,
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)',
          border: '1px solid #cbd5e1',
          minWidth: 260,
          bgcolor: '#ffffff',
        }
      }}
    >
      <Box sx={{ mb: 1, pb: 0.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {field === 'SOLICITADO' ? 'Detalle Solicitado por Divisa' : 'Detalle Total OC por Divisa'}
        </Typography>
        <Chip label="Detallado" size="small" sx={{ height: 16, fontSize: 8.5, fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {Object.entries(divisas).map(([divisa, vals]) => {
          const total      = vals.total     || 0;
          const solicitado = vals.solicitado || 0;
          const restante   = total > solicitado ? total - solicitado : (vals.restante || 0);
          const valToShow  = field === 'SOLICITADO' ? solicitado : total;

          const colorMain = divisa === 'USD' ? '#b45309' : divisa === 'EUR' ? '#4338ca' : '#0369a1';
          const bgBadge   = divisa === 'USD' ? '#fef3c7' : divisa === 'EUR' ? '#e0e7ff' : '#e0f2fe';

          return (
            <Box key={divisa} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, p: 0.8, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={divisa} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 900, color: colorMain, bgcolor: bgBadge, '& .MuiChip-label': { px: 0.8 } }} />
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#64748b' }}>({vals.count || 0} OCs)</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: field === 'SOLICITADO' ? '#16a34a' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  ${fmt(valToShow)}
                </Typography>
                {field === 'SOLICITADO' && (
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#ea580c' }}>
                    Restante: ${fmt(restante)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
});

// ── Pie de página con Totales para Móvil (Sticky & Expandible) ──────────────
const MobileTotalsFooter = React.memo(function MobileTotalsFooter({ divisasRef, totalCountRef, onOpenPopover }) {
  const [expanded, setExpanded] = useState(false);
  const divisas = divisasRef?.current || {};
  if (!divisas || Object.keys(divisas).length === 0) return null;

  const displayTotalCount = totalCountRef?.current || 0;
  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getBadgeStyle = (div) => {
    if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
    if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
    if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
    if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
    if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
    return { color: '#334155', bg: '#f1f5f9' };
  };

  const getCurrencySymbol = (div) => {
    if (div === 'EUR') return '€';
    if (div === 'GBP') return '£';
    return '$';
  };

  const divisasEntries = Object.entries(divisas || {}).sort((a, b) => {
    const order = { MXN: 1, USD: 2, EUR: 3 };
    return (order[a[0]] || 99) - (order[b[0]] || 99);
  });

  return (
    <Paper
      elevation={3}
      sx={{
        mt: 'auto',
        pt: 0,
        position: 'sticky',
        bottom: 2,
        zIndex: 10,
        borderRadius: '14px',
        border: '1.5px solid #cbd5e1',
        bgcolor: '#ffffff',
        boxShadow: '0 4px 18px rgba(15, 23, 42, 0.12)',
        overflow: 'hidden',
      }}
    >
      {/* Cabecera interactiva del pie móvil */}
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.4,
          py: 0.8,
          bgcolor: '#f1f5f9',
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
          '&:active': { bgcolor: '#e2e8f0' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: '#0f172a', letterSpacing: '0.02em' }}>
            TOTALES ({displayTotalCount} OCs)
          </Typography>
          <Chip
            label={`${divisasEntries.length} Divisas`}
            size="small"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#0284c7' }}>
            {expanded ? 'Ocultar' : 'Ver detalle'}
          </Typography>
          <IconButton size="small" sx={{ p: 0.1, color: '#0284c7' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Resumen en 1 sola fila cuando está comprimido */}
      {!expanded && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            px: 1.4,
            py: 0.7,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            bgcolor: '#ffffff',
            cursor: 'pointer'
          }}
        >
          {divisasEntries.map(([divisa, vals]) => {
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);
            return (
              <Box key={divisa} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.56rem', fontWeight: 900, color, bgcolor: bg, px: 0.45, py: 0.1, borderRadius: '4px' }}>
                  {divisa}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 850, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  {sym}{fmt(vals.total || 0)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Desglose completo cuando está expandido */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.2, bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {divisasEntries.map(([divisa, vals]) => {
            const total = vals.total || 0;
            const sol = vals.solicitado || 0;
            const rest = vals.restante !== undefined ? vals.restante : (total > sol ? total - sol : 0);
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);

            return (
              <Box
                key={divisa}
                sx={{
                  p: 1,
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  bgcolor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1
                }}
              >
                {/* Columna Izquierda: Total OC / Divisa */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenPopover) onOpenPopover(e.currentTarget, 'TOTAL');
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer' }}
                >
                  <Typography sx={{ fontSize: '0.64rem', fontWeight: 900, color, bgcolor: bg, px: 0.6, py: 0.2, borderRadius: '5px' }}>
                    {divisa}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: '0.60rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                      Total OC
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                      {sym}{fmt(total)}
                    </Typography>
                  </Box>
                </Box>

                {/* Columna Derecha: Montos Solicitado y Restante */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenPopover) onOpenPopover(e.currentTarget, 'SOLICITADO');
                  }}
                  sx={{ textAlign: 'right', cursor: 'pointer' }}
                >
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                    Sol: {sym}{fmt(sol)}
                  </Typography>
                  {rest > 0 && (
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#ea580c', fontVariantNumeric: 'tabular-nums', mt: 0.1 }}>
                      Rest: {sym}{fmt(rest)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
});


const getUniqueOptions = (values) => (
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
);

const buildCentroCostoOption = (row = {}) => {
  const numero = String(row.NUMEROCC || row.NUMERO_CENTRO_COSTO || row.NUMERO_CC || '').trim();
  const nombre = String(row.CENTROCOSTO || row.CENTRO_COSTO || row.NOMBRE_CC || '').trim();

  if (numero && nombre && numero.toUpperCase() !== nombre.toUpperCase()) return `${numero} - ${nombre}`;
  return numero || nombre;
};

const parseMoney = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const s = String(value).trim().replace(/[^\d.,-]/g, '').replace(/,/g, '');
  if (!s || s === '-' || s === '.' || s === '-.') return NaN;
  return Number(s);
};

const splitCentroCostoOption = (value = '') => (
  String(value)
    .split(' - ')
    .map(part => part.trim())
    .filter(Boolean)
);

const getCentroCostoFilterValue = (value = '') => {
  const parts = splitCentroCostoOption(value);
  return parts.length > 1 ? parts[parts.length - 1] : String(value).trim();
};

const getCentroCostoFilterValues = (values = []) => (
  values
    .map(getCentroCostoFilterValue)
    .filter(Boolean)
);

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) {
    const first = values[0];
    return typeof first === 'string' ? first : first?.nombre || String(first);
  }
  return `${values.length} seleccionadas`;
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);
  const isSelected = Boolean(summary);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 24px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: '#0655A5',
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        ...(isSelected ? {
          '& .MuiInputBase-root': {
            bgcolor: '#f0f7ff !important',
            borderColor: '#93c5fd !important',
            '& fieldset': {
              borderColor: '#60a5fa !important',
              borderWidth: '1.5px !important',
            }
          }
        } : {})
      }}
    />
  );
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" sx={{ fontSize: 16, color: '#94a3b8' }} />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ fontSize: 16, color: '#0655A5' }} />;

const renderCheckboxOption = (props, option, { selected }) => (
  <li
    {...props}
    style={{
      padding: '4px 8px',
      fontSize: '0.74rem',
      fontWeight: selected ? 700 : 500,
      backgroundColor: selected ? '#e0f2fe' : 'transparent',
      color: selected ? '#0369a1' : '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      borderBottom: '1px solid #f8fafc',
    }}
  >
    <Checkbox
      icon={icon}
      checkedIcon={checkedIcon}
      style={{ marginRight: 4, padding: 0 }}
      checked={selected}
      size="small"
    />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
      {typeof option === 'string' ? option : option?.nombre || ''}
    </Box>
  </li>
);

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: 28,
    minHeight: 28,
    maxHeight: 28,
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: '0.68rem',
    fontWeight: 500,
    px: '10px !important',
    py: '0px !important',
    flexWrap: 'nowrap',
    overflow: 'visible',
    '& fieldset': { 
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
  },
  '& .MuiAutocomplete-input': {
    minWidth: '24px !important',
    py: '0px !important',
    fontSize: '0.68rem',
    color: '#0f172a',
    '&::placeholder': {
      color: '#64748b',
      opacity: 1,
      fontWeight: 500,
    }
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
};

const compactTextFieldSx = {
  '& .MuiInputBase-root': {
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.55,
    fontSize: 13,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
};

const normalizeOptionText = (value = '') => (
  String(value ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);

const matchesAnyOption = (value, selected = []) => {
  if (!selected.length) return true;
  const normalizedValue = normalizeOptionText(value);
  return selected.some(item => normalizeOptionText(item) === normalizedValue);
};

const ordenCompraStatusChips = [
  { label: 'Sin solicitud', value: 'Sin solicitud', bg: '#ffffff', color: '#475569', help: 'Orden sin solicitud registrada' },
  { label: 'Sol. parcial', value: 'Solicitada Parcial', bg: '#e0f2fe', color: '#0369a1', help: 'Solicitud creada por una parte del total' },
  { label: 'Sol. total', value: 'Solicitada Total', bg: '#ede9fe', color: '#5b21b6', help: 'Solicitud creada por el total de la orden' },
  { label: 'Pag. parcial', value: 'Pagada Parcial', bg: '#99f6e4', color: '#0d5d56', help: 'Tiene pago o comprobante parcial' },
  { label: 'Pag. total', value: 'Pagada Total', bg: '#dcfce7', color: '#15803d', help: 'Tiene pago o comprobante por el total' },
  { label: 'Cancelada ERP', value: 'Cancelada ERP,Cancelada', bg: '#fee2e2', color: '#991b1b', help: 'Cancelada desde ERP o Sistema' },
];

export default function Solicitudes() {
  const apiUrl = config.apiBaseUrl;

  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);

  const {
    data,
    periodKpis,
    loadingData,
    loadingSpinner,
    spinnerMessage,
    selectedRow,
    modalSolicitudesOpen,
    tempFilterData,
    filterData,
    baseFilters,
    usuarioId,
    idPerfil,
    empresaIdPref,
    empresaIds,
    nombreEmpresa,
    emptyStateData,

    setTempFilterData,
    setFilterData,
    setTableParams,
    setLoadingSpinner,

    aplicarFiltrosExternos,
    updateVisibleFilter,
    handleImport,
    handleOpenSolicitudes,
    handleCloseSolicitudes,
    handleSolicitudGuardada,
    handleExportFetchAll,
    handleTableStateChange,
  } = useSolicitudesController(apiUrl, { centrosCostoCatalogo });

  const [quickSearch, setQuickSearch] = useState('');
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [filterOptionRows, setFilterOptionRows] = useState([]);
  const [openFilter, setOpenFilter] = useState(''); // cual autocomplete está abierto
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [showAnalyticsCards, setShowAnalyticsCards] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 900;
    }
    return false;
  });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverActiveField, setPopoverActiveField] = useState(null);

  const selectedCatalogEmpresaIds = useMemo(() => {
    const selected = Array.isArray(tempFilterData.EMPRESA_IDS)
      ? tempFilterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scopedSelected = selected.filter(id => empresaIds.includes(id));
    if (scopedSelected.length) return scopedSelected;
    return empresaIds;
  }, [empresaIds, tempFilterData.EMPRESA_IDS]);

  const applyFilters = useCallback((nextFilters = tempFilterData, nextQuickSearch = quickSearch) => {
    aplicarFiltrosExternos(nextFilters, nextQuickSearch);
  }, [aplicarFiltrosExternos, quickSearch, tempFilterData]);

  const handleStatusChipFilter = useCallback((value) => {
    const updated = { ...tempFilterData, ESTATUS: value || '' };
    setTempFilterData(updated);
    aplicarFiltrosExternos(updated, quickSearch);
  }, [aplicarFiltrosExternos, quickSearch, setTempFilterData, tempFilterData]);

  const handleStatusFilterClick = useCallback((status) => {
    const newVal = tempFilterData.ESTATUS === status ? '' : status;
    const updated = { ...tempFilterData, ESTATUS: newVal };
    setTempFilterData(updated);
    aplicarFiltrosExternos(updated, quickSearch);
  }, [aplicarFiltrosExternos, quickSearch, setTempFilterData, tempFilterData]);

  const [hierarchicalAnchorEl, setHierarchicalAnchorEl] = useState(null);

  const statusChipsMemo = useMemo(() => {
    return ordenCompraStatusChips.map(c => ({
      ...c,
      status: c.value,
    }));
  }, []);

  useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;

      try {
        const response = await axios.get(`${apiUrl}/api/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }))
          .filter(empresa => empresaIds.includes(empresa.id));
        setEmpresaOptions(mapped);

        if (mapped.length === 1) {
          const yaTieneEmpresa = (tempFilterData.EMPRESA_IDS || []).includes(mapped[0].id);
          if (!yaTieneEmpresa) {
            const updated = { ...tempFilterData, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre };
            setTempFilterData(updated);
            setFilterData(updated);
          }
        }
      } catch (err) {
        console.error('Error cargando empresas para ordenes de compra:', err);
        const fallback = empresaIds.length === 1
          ? [{ id: empresaIds[0], nombre: nombreEmpresa || `Empresa ${empresaIds[0]}` }]
          : [];
        setEmpresaOptions(fallback);
      }
    };

    cargarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, usuarioId]);

  // Cache para evitar recargar catálogos si no cambiaron las empresas seleccionadas
  const lastLoadedCatalogEmpresasRef = useRef(null);

  // Cargar Catálogos de Proyectos, Divisiones y Centros de Costo (acotados a los permisos del usuario)
  useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;

    const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
    if (lastLoadedCatalogEmpresasRef.current === empresasKey) return;
    lastLoadedCatalogEmpresasRef.current = empresasKey;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId, idPerfil]);

  const lastLoadedOpcionesEmpresasRef = useRef(null);

  useEffect(() => {
    const cargarOpciones = async () => {
      if (!apiUrl || !selectedCatalogEmpresaIds.length) {
        setFilterOptionRows([]);
        lastLoadedOpcionesEmpresasRef.current = null;
        return;
      }

      const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
      if (lastLoadedOpcionesEmpresasRef.current === empresasKey) return;
      lastLoadedOpcionesEmpresasRef.current = empresasKey;

      try {
        const response = await axios.post(`${apiUrl}/solicitudesAdmin/opcionesFiltros`, {
          idEmpresa: selectedCatalogEmpresaIds,
          filterData: {},
        });
        const rows = response.data?.rows || [];
        if (rows.length) {
          setFilterOptionRows(rows);
          return;
        }

        const departamentos = response.data?.departamentos || [];
        const centrosCosto = response.data?.centrosCosto || [];
        const proveedores = response.data?.proveedores || [];
        const divisas = response.data?.divisas || [];
        setFilterOptionRows([
          ...departamentos.map(DEPARTAMENTO => ({ DEPARTAMENTO })),
          ...centrosCosto.map(CENTROCOSTO => ({ CENTROCOSTO })),
          ...proveedores.map(PROVEEDOR => ({ PROVEEDOR })),
          ...divisas.map(DIVISA => ({ DIVISA })),
        ]);
      } catch (err) {
        console.error('Error cargando opciones de filtros para ordenes de compra:', err);
        setFilterOptionRows([]);
      }
    };

    cargarOpciones();
  }, [apiUrl, selectedCatalogEmpresaIds]);

  // ─── Opciones Dinámicas en Cascada Jerárquica ───
  // 1. Proyectos disponibles (filtrados por los centros de costo a los que el usuario tiene acceso)
  const proyectoOptions = useMemo(() => {
    const allowedProyIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      if (centrosCostoCatalogo.length === 0 || allowedProyIds.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo]);

  // 2. Grupos / Divisiones (filtrados por los proyectos seleccionados y por los CCs permitidos del usuario)
  const divisionOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const allowedDivIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const pId = Number(d.ID_PROYECTO);
      const dId = Number(d.ID_DIVISION);
      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pId);
      const matchAllowed = centrosCostoCatalogo.length === 0 || allowedDivIds.has(dId);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(dId, d.NOMBRE_DIVISION);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [divisionesCatalogo, tempFilterData.PROYECTOS, centrosCostoCatalogo]);

  const optionSourceRows = useMemo(() => (
    filterOptionRows.length ? filterOptionRows : (Array.isArray(data?.contenido) ? data.contenido : [])
  ), [data?.contenido, filterOptionRows]);

  const getOptionRows = useCallback((excludeField = '') => {
    const selectedDepartamentos = tempFilterData.DEPARTAMENTOS || [];
    const selectedCentros = getCentroCostoFilterValues(tempFilterData.CENTRO_COSTOS || []);
    const selectedProveedores = tempFilterData.PROVEEDORES || [];
    const selectedDivisas = tempFilterData.DIVISAS || [];
    const selectedCompradoras = tempFilterData.COMPRADORAS || [];

    return optionSourceRows.filter(row => {
      const centroNombre = getCentroCostoFilterValue(buildCentroCostoOption(row));

      return (
        (excludeField === 'DEPARTAMENTOS' || matchesAnyOption(row.DEPARTAMENTO, selectedDepartamentos)) &&
        (excludeField === 'CENTRO_COSTOS' || matchesAnyOption(centroNombre, selectedCentros)) &&
        (excludeField === 'PROVEEDORES' || matchesAnyOption(row.PROVEEDOR, selectedProveedores)) &&
        (excludeField === 'DIVISAS' || matchesAnyOption(row.DIVISA, selectedDivisas)) &&
        (excludeField === 'COMPRADORAS' || matchesAnyOption(row.COMPRADOR, selectedCompradoras))
      );
    });
  }, [optionSourceRows, tempFilterData]);

  const departamentoOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DEPARTAMENTOS').map(row => row.DEPARTAMENTO))
  ), [getOptionRows]);

  // 3. Centros de Costo en Cascada
  const centroCostoOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (tempFilterData.DIVISIONES || []).map(Number);

    if (centrosCostoCatalogo.length) {
      const filtered = centrosCostoCatalogo.filter(cc => {
        const pId = Number(cc.ID_PROYECTO);
        const dId = Number(cc.ID_DIVISION);

        if (selectedDivIds.length && !selectedDivIds.includes(dId)) return false;
        if (selectedProyIds.length && !selectedProyIds.includes(pId)) return false;
        return true;
      });

      const options = filtered.map(buildCentroCostoOption);
      return getUniqueOptions(options);
    }

    return getUniqueOptions(getOptionRows('CENTRO_COSTOS').map(buildCentroCostoOption));
  }, [centrosCostoCatalogo, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, getOptionRows]);

  // Resumen visual estilo Breadcrumbs: Empresa > Proyecto > Grupo > CC
  const hierarchicalSummary = useMemo(() => {
    const parts = [];
    const empCount = (tempFilterData.EMPRESA_IDS || []).length;
    if (empCount === 1) {
      const emp = empresaOptions.find(e => e.id === tempFilterData.EMPRESA_IDS[0]);
      if (emp) parts.push(emp.nombre);
    } else if (empCount > 1) {
      parts.push(`${empCount} Emp`);
    }

    const proyCount = (tempFilterData.PROYECTOS || []).length;
    if (proyCount === 1) {
      const proy = proyectoOptions.find(p => p.id === tempFilterData.PROYECTOS[0]);
      if (proy) parts.push(proy.nombre);
    } else if (proyCount > 1) {
      parts.push(`${proyCount} Proy`);
    }

    const divCount = (tempFilterData.DIVISIONES || []).length;
    if (divCount === 1) {
      const div = divisionOptions.find(d => d.id === tempFilterData.DIVISIONES[0]);
      if (div) parts.push(div.nombre);
    } else if (divCount > 1) {
      parts.push(`${divCount} Grupos`);
    }

    const ccCount = (tempFilterData.CENTRO_COSTOS || []).length;
    if (ccCount === 1) {
      parts.push(tempFilterData.CENTRO_COSTOS[0].split(' - ')[0] || tempFilterData.CENTRO_COSTOS[0]);
    } else if (ccCount > 1) {
      parts.push(`${ccCount} CCs`);
    }

    if (!parts.length) return 'Empresa / Proyecto / CC';
    if (empCount > 0 && !proyCount && !divCount && !ccCount) {
      return `${parts[0]} › Proyecto / CC`;
    }
    return parts.join(' › ');
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS, empresaOptions, proyectoOptions, divisionOptions]);

  const hasHierarchicalFilter = useMemo(() => {
    return (tempFilterData.EMPRESA_IDS || []).length > 0 ||
      (tempFilterData.PROYECTOS || []).length > 0 ||
      (tempFilterData.DIVISIONES || []).length > 0 ||
      (tempFilterData.CENTRO_COSTOS || []).length > 0;
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS]);

  const extraFiltersCount = useMemo(() => {
    let count = 0;
    if ((tempFilterData.DEPARTAMENTOS || []).length) count += tempFilterData.DEPARTAMENTOS.length;
    if ((tempFilterData.PROVEEDORES || []).length) count += tempFilterData.PROVEEDORES.length;
    if ((tempFilterData.DIVISAS || []).length) count += tempFilterData.DIVISAS.length;
    if ((tempFilterData.COMPRADORAS || []).length) count += tempFilterData.COMPRADORAS.length;
    return count;
  }, [tempFilterData.DEPARTAMENTOS, tempFilterData.PROVEEDORES, tempFilterData.DIVISAS, tempFilterData.COMPRADORAS]);

  const hasAnyActiveFilter = useMemo(() => {
    return (
      hasHierarchicalFilter ||
      extraFiltersCount > 0 ||
      Boolean(quickSearch) ||
      Boolean(tempFilterData.ESTATUS) ||
      (tempFilterData.FECHA_INICIO && tempFilterData.FECHA_INICIO !== baseFilters?.FECHA_INICIO) ||
      (tempFilterData.FECHA_FIN && tempFilterData.FECHA_FIN !== baseFilters?.FECHA_FIN)
    );
  }, [hasHierarchicalFilter, extraFiltersCount, quickSearch, tempFilterData.ESTATUS, tempFilterData.FECHA_INICIO, tempFilterData.FECHA_FIN, baseFilters]);

  const proveedorOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('PROVEEDORES').map(row => row.PROVEEDOR))
  ), [getOptionRows]);

  const divisaOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DIVISAS').map(row => row.DIVISA))
  ), [getOptionRows]);

  const compradoraOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('COMPRADORAS').map(row => row.COMPRADOR))
  ), [getOptionRows]);

  const compactTableHiddenFields = useMemo(() => ([
    'FOLIO', 'FOLIO_OC', 'ORDEN', 'REQUI', 'EMPRESA', 'NOMBRE_EMPRESA',
    'FECHA', 'FECHA_OC', 'FECHA_ORDENCOMPRA', 'FECHASOLICITUD',
    'PROVEEDOR', 'COMPRADOR', 'SOLICITANTE', 'REQUISITOR',
    'CENTROCOSTO', 'CENTROCOSTOS', 'NOMBRE_CC', 'NUMEROCC', 'NUMERO_CC',
    'DEPARTAMENTO', 'TOTAL', 'TOTAL_OC', 'DIVISA', 'MONEDA',
    'MONTOSOLICITADO', 'MONTO_SOLICITADO', 'PENDIENTE', 'RESTANTE',
    'PAGADO', 'PAGO_ACUM', 'ABONOS',
    'FOLIO_FACTURA', 'FACTURA', 'FECHA_FACTURA',
    'PAGO', 'TIPO_PAGO', 'CONDICION_PAGO', 'NUMERO_ECONOMICO', 'ECONOMICO',
    'NOTAS', 'NOTAS_REQUI', 'NOTA',
    'ESTATUS', 'ID',
    'TIENE_SOLICITUD', 'SOLICITUDES_COUNT', 'SOLICITADO',
    'COMPROBANTES_PAGO_COUNT', 'COMPROBANTES_PAGO_MONTO', 'ESTATUS_ORIGINAL',
  ]), []);

  const tableHiddenFieldsMemo = useMemo(() => ([
    'DESCRIPCION_PART',
    'IDDATOSERP',
    'EMPRESAID',
    'FACTURA_ANTES_PAGO',
    'COINCIDE_FORMULA',
    'FECHA_FACTURA',
    'CANCELADO_ERP',
    'DIASRESTANTES',
    ...compactTableHiddenFields,
  ]), [compactTableHiddenFields]);

  const tableColumnLabels = useMemo(() => ({
    OC_REQUI: 'OC / Requi',
    FECHA_SOLICITANTE: 'Fecha / Requisitor',
    PROVEEDOR_COMPRADOR: 'Proveedor / Comprador',
    CENTRO_DEPTO: 'Centro / Departamento',
    TOTAL_DIVISA: 'Total OC / Divisa',
    FACTURA_PAGO: 'Factura / Num. Eco',
    MONTOS_RESUMEN: 'Montos',
    PAGO: 'Condicion Pago',
    NUMERO_ECONOMICO: 'Economico',
    ESTATUS_COMPACTO: 'Estatus',
    FECHA_SOLICITUD_PAGO: 'Fecha Solicitud Pago',
    ULTIMA_SOLICITUD: 'Última Solicitud',
  }), []);

  const compactColumnOrder = useMemo(() => ([
    'OC_REQUI',
    'FECHA_SOLICITANTE',
    'PROVEEDOR_COMPRADOR',
    'CENTRO_DEPTO',
    'FACTURA_PAGO',
    'TOTAL_DIVISA',
    'MONTOS_RESUMEN',
    'ESTATUS_COMPACTO',
    'FECHA_SOLICITUD_PAGO',
    'ULTIMA_SOLICITUD',
  ]), []);

  const defaultHiddenCols = useMemo(() => ([
    'FECHA_SOLICITUD_PAGO',
    'ULTIMA_SOLICITUD',
  ]), []);

  const dataForTable = useMemo(() => {
    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    return {
      ...data,
      contenido: contenido.map((row) => {
        // Objeto plano base con los campos visibles compactos
        const compactRow = {
          ...row,
          OC_REQUI: '',
          FECHA_SOLICITANTE: '',
          PROVEEDOR_COMPRADOR: '',
          CENTRO_DEPTO: '',
          FACTURA_PAGO: '',
          TOTAL_DIVISA: '',
          MONTOS_RESUMEN: '',
          ESTATUS_COMPACTO: row.ESTATUS || '',
        };

        if (row.partidas || row.PARTIDAS) {
          compactRow.partidas = row.partidas || row.PARTIDAS;
        }

        return compactRow;
      }),
    };
  }, [data]);

  const kpisSummary = useMemo(() => {
    // Si ya tenemos los KPIs globales del periodo calculados, los usamos para que no varíen por página
    if (periodKpis && Object.keys(periodKpis.divisas).length > 0) {
      return {
        ...periodKpis,
        totalCount: data?.total || periodKpis.totalCount || 0,
      };
    }

    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    const divisasAcc = {};
    let sinSolicitudCount = 0;
    let sinSolicitudMonto = 0;
    let solParcialCount = 0;
    let solParcialMonto = 0;
    let solTotalCount = 0;
    let solTotalMonto = 0;
    let pagadasCount = 0;
    let totalActiveCount = 0;

    contenido.forEach((row) => {
      // Excluir órdenes canceladas del conteo y sumas de KPIs
      const isCancelada = Number(row.CANCELADO_ERP) === 1 ||
                          Number(row.CANCELADO) === 1 ||
                          String(row.ESTATUS || '').toUpperCase().includes('CANCELAD');
      if (isCancelada) return;

      const rawDivisa = String(row.DIVISA || row.MONEDA || 'MXN').trim().toUpperCase();
      const divisa = rawDivisa.includes('DOL') || rawDivisa.includes('USD') ? 'USD'
        : rawDivisa.includes('EUR') ? 'EUR' : 'MXN';

      if (!divisasAcc[divisa]) {
        divisasAcc[divisa] = { total: 0, solicitado: 0, restante: 0, count: 0 };
      }

      const totalOC = parseMoney(row.TOTAL ?? row.TOTAL_OC ?? 0) || 0;
      const solicitado = parseMoney(row.MONTOSOLICITADO ?? row.MONTO_SOLICITADO ?? 0) || 0;

      divisasAcc[divisa].total += totalOC;
      divisasAcc[divisa].solicitado += solicitado;
      divisasAcc[divisa].restante += (totalOC > solicitado ? totalOC - solicitado : 0);
      divisasAcc[divisa].count += 1;
      totalActiveCount += 1;

      // Estatus operativo
      const estatus = String(row.ESTATUS || '').trim().toUpperCase();
      if (!estatus || estatus.includes('SIN') || solicitado <= 0) {
        sinSolicitudCount += 1;
        sinSolicitudMonto += totalOC;
      } else if (estatus.includes('PARCIAL') || (solicitado > 0 && solicitado < totalOC)) {
        solParcialCount += 1;
        solParcialMonto += totalOC;
      } else if (estatus.includes('PAGAD') || estatus.includes('PAGO')) {
        pagadasCount += 1;
      } else {
        solTotalCount += 1;
        solTotalMonto += totalOC;
      }
    });

    // Estimación global en MXN (tipo de cambio referencia aprox: USD ~18.50, EUR ~20.00)
    const TASA_USD_MXN = 18.50;
    const TASA_EUR_MXN = 20.00;
    const totalEstimadoMXN = (divisasAcc['MXN']?.total || 0) + 
      ((divisasAcc['USD']?.total || 0) * TASA_USD_MXN) + 
      ((divisasAcc['EUR']?.total || 0) * TASA_EUR_MXN);

    return {
      totalCount: totalActiveCount,
      pageCount: totalActiveCount,
      divisas: divisasAcc,
      totalEstimadoMXN,
      funnel: {
        sinSolicitud: { count: sinSolicitudCount, monto: sinSolicitudMonto },
        solParcial: { count: solParcialCount, monto: solParcialMonto },
        solTotal: { count: solTotalCount, monto: solTotalMonto },
        pagadas: { count: pagadasCount },
        enTramiteCount: solParcialCount + solTotalCount,
      }
    };
  }, [data, periodKpis]);

  // Refs que apuntan siempre a los datos más recientes sin crear dependencias en useCallback
  const kpisDivisasRef = useRef({});
  const kpisFunnelRef  = useRef({});
  const kpisTotalCountRef = useRef(0);
  kpisDivisasRef.current = kpisSummary.divisas  || {};
  kpisFunnelRef.current  = kpisSummary.funnel   || {};
  kpisTotalCountRef.current = kpisSummary.totalCount || 0;

  // Ref al componente TotalesPopover — abrirlo NO re-renderiza Solicitudes
  const totalesPopoverRef = useRef(null);

  const handleOpenFooterPopover = useCallback((el, fieldKey) => {
    totalesPopoverRef.current?.open(el, fieldKey);
  }, []);

  const renderFooterRowMemo = useCallback(({ orderedKeys, stickyLeftMeta, hasActions, selectable, hasPartidas }) => {
    const divisas = kpisDivisasRef.current;
    if (!divisas || Object.keys(divisas).length === 0) return null;

    const displayTotalCount = kpisTotalCountRef.current || 0;

    const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getBadgeStyle = (div) => {
      if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
      if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
      if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
      if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
      if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
      return { color: '#334155', bg: '#f1f5f9' };
    };

    const getCurrencySymbol = (div) => {
      if (div === 'EUR') return '€';
      if (div === 'GBP') return '£';
      return '$';
    };

    const divisasEntries = Object.entries(divisas || {}).sort((a, b) => {
      const order = { MXN: 1, USD: 2, EUR: 3 };
      return (order[a[0]] || 99) - (order[b[0]] || 99);
    });

    return (
      <tfoot
        style={{ position: 'sticky', bottom: 0, zIndex: 12, backgroundColor: '#f1f5f9' }}
        onClick={(e) => e.stopPropagation()}
      >
        <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', boxShadow: '0 -4px 12px rgba(15, 23, 42, 0.04)' }}>
          {selectable && <td style={{ backgroundColor: '#f1f5f9', padding: '2px 4px' }} />}
          {hasPartidas && <td style={{ backgroundColor: '#f1f5f9', padding: '2px 4px' }} />}

          {orderedKeys.map((key) => {
            const isSticky = stickyLeftMeta && stickyLeftMeta[key];
            const stickyStyle = isSticky ? {
              position: 'sticky',
              left: isSticky.left,
              zIndex: 13,
              backgroundColor: '#f1f5f9',
              boxShadow: isSticky.isLastSticky ? '3px 0 6px -2px rgba(0, 0, 0, 0.12)' : 'none',
            } : {};

            const isFirstCol = key === orderedKeys[0];
            let content = null;
            let isClickable = false;
            let fieldKey = null;

            if (isFirstCol) {
              content = (
                <Typography sx={{ fontWeight: 850, fontSize: '0.60rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  TOTALES ({displayTotalCount})
                </Typography>
              );
            } else if (key === 'TOTAL_DIVISA' || key === 'TOTAL') {
              isClickable = true;
              fieldKey = 'TOTAL';
              content = (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5px', py: 0.1 }}>
                  {divisasEntries.map(([divisa, vals]) => {
                    const total = vals.total || 0;
                    const sol = vals.solicitado || 0;
                    const rest = vals.restante !== undefined ? vals.restante : (total > sol ? total - sol : 0);
                    const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                    const sym = getCurrencySymbol(divisa);
                    return (
                      <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.35, py: 0.05, borderRadius: '2px', minWidth: 20, textAlign: 'center', lineHeight: 1.1 }}>
                            {divisa}
                          </Typography>
                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: total > 0 ? '#0f172a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {sym}{fmt(total)}
                          </Typography>
                        </Box>
                        {rest > 0 && (
                          <Typography sx={{ fontSize: '0.48rem', visibility: 'hidden', lineHeight: 1, mt: '0.5px' }}>
                            -
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            } else if (key === 'MONTOS_RESUMEN' || key === 'MONTOSOLICITADO' || key === 'SOLICITADO') {
              isClickable = true;
              fieldKey = 'SOLICITADO';
              content = (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5px', py: 0.1 }}>
                  {divisasEntries.map(([divisa, vals]) => {
                    const total = vals.total || 0;
                    const sol = vals.solicitado || 0;
                    const rest = vals.restante !== undefined ? vals.restante : (total > sol ? total - sol : 0);
                    const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                    const sym = getCurrencySymbol(divisa);
                    return (
                      <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.35, py: 0.05, borderRadius: '2px', minWidth: 20, textAlign: 'center', lineHeight: 1.1 }}>
                            {divisa}
                          </Typography>
                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: sol > 0 ? '#16a34a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {sym}{fmt(sol)}
                          </Typography>
                        </Box>
                        {rest > 0 && (
                          <Typography sx={{ fontSize: '0.50rem', fontWeight: 750, color: '#ea580c', fontVariantNumeric: 'tabular-nums', pl: '24px', mt: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                            Rest: {sym}{fmt(rest)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            }

            return (
              <td
                key={key}
                onClick={(e) => { e.stopPropagation(); if (isClickable) handleOpenFooterPopover(e.currentTarget, fieldKey); }}
                style={{
                  ...stickyStyle,
                  padding: '2px 6px',
                  backgroundColor: isClickable ? '#f1f5f9' : '#f8fafc',
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                  verticalAlign: 'middle',
                }}
              >
                {content}
              </td>
            );
          })}

          {hasActions && <td style={{ backgroundColor: '#f1f5f9', padding: '4px 6px' }} />}
        </tr>
      </tfoot>
    );
  // Sin dependencias en kpisSummary — lee siempre desde los refs sin re-crear la función
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOpenFooterPopover]);

  const renderMobileFooterMemo = useCallback(() => {
    return (
      <MobileTotalsFooter
        divisasRef={kpisDivisasRef}
        totalCountRef={kpisTotalCountRef}
        onOpenPopover={handleOpenFooterPopover}
      />
    );
  }, [handleOpenFooterPopover]);

  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Generar Solicitud',
      icon: <RequestQuoteIcon />,
      buttonColor: '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: false,
      onClick: (row) => handleOpenSolicitudes(row),
    },
  ], [handleOpenSolicitudes]);

  const resetFilters = useCallback(() => {
    setTempFilterData(baseFilters);
    setFilterData(baseFilters);
    setQuickSearch('');
    aplicarFiltrosExternos(baseFilters, '');
  }, [aplicarFiltrosExternos, baseFilters, setFilterData, setTempFilterData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, width: '100%', overflow: 'hidden' }}>
      {/* ── BARRA SUPERIOR DE ACCESO RÁPIDO & TOGGLE DE FILTROS ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.8, mb: 0.5, gap: 1, minWidth: 0, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, width: '100%' }}>

          {/* Vista Móvil Dedicada (xs a md): 2 Filas ergonómicas y optimizadas para touch */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 0.8,
              width: '100%',
              bgcolor: '#ffffff',
              p: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
            }}
          >
            {/* Fila 1 Móvil: Buscador expandido + Jerarquía + Filtros + Sincronizar + KPIs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: '100%' }}>
              <TextField
                disabled={loadingData}
                size="small"
                placeholder="Buscar OC, folio, prov..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFilters(tempFilterData, e.currentTarget.value);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#0655A5', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  endAdornment: quickSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={loadingData}
                        size="small"
                        onClick={() => {
                          setQuickSearch('');
                          applyFilters(tempFilterData, '');
                        }}
                        sx={{ p: 0.2 }}
                      >
                        <ClearIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: '#f8fafc',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    px: 0.8,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                    '&.Mui-focused fieldset': { borderColor: '#0655A5', bgcolor: '#ffffff' },
                  }
                }}
              />

              {/* Botón Jerarquía Móvil */}
              <Tooltip title={`Jerarquía: ${hierarchicalSummary}`}>
                <Button
                  size="small"
                  variant={hasHierarchicalFilter ? 'contained' : 'outlined'}
                  onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 34,
                    width: 34,
                    height: 34,
                    p: 0,
                    borderRadius: '10px',
                    bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                    borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                    color: hasHierarchicalFilter ? '#1e40af' : '#475569',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc' },
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>

              {/* Botón Filtros Complementarios Móvil con Badge */}
              <Tooltip title="Otros filtros (Depto, Prov, Divisa, Comprador)">
                <Badge
                  badgeContent={extraFiltersCount}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', height: 16, minWidth: 16 } }}
                >
                  <Button
                    size="small"
                    variant={showFiltersPanel || extraFiltersCount > 0 ? 'contained' : 'outlined'}
                    onClick={(e) => {
                      setFilterAnchorEl(e.currentTarget);
                      setShowFiltersPanel(prev => !prev);
                    }}
                    sx={{
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      p: 0,
                      borderRadius: '10px',
                      bgcolor: showFiltersPanel ? '#0f172a' : extraFiltersCount > 0 ? '#0655A5' : '#ffffff',
                      borderColor: extraFiltersCount > 0 ? '#0655A5' : '#cbd5e1',
                      color: showFiltersPanel || extraFiltersCount > 0 ? '#ffffff' : '#475569',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#054785' },
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 17 }} />
                  </Button>
                </Badge>
              </Tooltip>

              {/* Sincronizar Móvil */}
              <SyncDialog
                onSync={handleImport}
                defaultFechaInicio={tempFilterData.FECHA_INICIO}
                defaultFechaFin={tempFilterData.FECHA_FIN}
                defaultFolio={tempFilterData.FOLIO}
                isCompact={true}
                iconOnly={true}
              />

              {/* KPIs Toggle Móvil */}
              <Tooltip title={showAnalyticsCards ? 'Ocultar Analíticas' : 'Ver Analíticas'}>
                <Button
                  size="small"
                  variant={showAnalyticsCards ? 'contained' : 'outlined'}
                  onClick={() => setShowAnalyticsCards(prev => !prev)}
                  sx={{
                    minWidth: 34,
                    width: 34,
                    height: 34,
                    p: 0,
                    borderRadius: '10px',
                    bgcolor: showAnalyticsCards ? '#0284c7' : '#ffffff',
                    borderColor: showAnalyticsCards ? '#0284c7' : '#cbd5e1',
                    color: showAnalyticsCards ? '#ffffff' : '#0284c7',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: showAnalyticsCards ? '#0369a1' : '#f0f9ff' },
                  }}
                >
                  <AnalyticsIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>
            </Box>

            {/* Fila 2 Móvil: Rango de fechas horizontal + Menú Estatus + Restablecer */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                width: '100%',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {/* Fechas en cápsula táctil */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  bgcolor: '#f8fafc',
                  px: 0.8,
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  height: 28,
                  flexShrink: 0,
                  '&:hover': { borderColor: '#94a3b8' },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 13, color: '#0655A5', mr: 0.2 }} />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
                <TextField
                  disabled={loadingData}
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_INICIO}
                  onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                  onClick={(e) => {
                    try {
                      if (e.target?.showPicker) e.target.showPicker();
                    } catch {}
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 78,
                    '& .MuiInputBase-input': {
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': { display: 'none' },
                      '&::-webkit-inner-spin-button': { display: 'none' },
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
                <TextField
                  disabled={loadingData}
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_FIN}
                  onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                  onClick={(e) => {
                    try {
                      if (e.target?.showPicker) e.target.showPicker();
                    } catch {}
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 78,
                    '& .MuiInputBase-input': {
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': { display: 'none' },
                      '&::-webkit-inner-spin-button': { display: 'none' },
                    },
                  }}
                />
              </Box>

              {/* Menú Desplegable de Estatus */}
              <TableFilterChips
                items={statusChipsMemo}
                activeFilter={tempFilterData.ESTATUS || ''}
                onFilterClick={handleStatusFilterClick}
                disabled={loadingData}
                isCompact={true}
              />

              {/* Botón Exportar a Excel Móvil */}
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <ExportButton
                  data={data || []}
                  onFetchAll={handleExportFetchAll}
                  hidePendientes={true}
                  tableLoading={loadingData}
                  btnSx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    border: '1px solid #86efac',
                    bgcolor: '#f0fdf4',
                    color: '#16a34a',
                    boxShadow: 'none',
                    p: 0,
                  }}
                />
              </Box>

              {/* Botón rápido Restablecer si hay algún filtro activo */}
              {hasAnyActiveFilter && (
                <Button
                  size="small"
                  variant="text"
                  onClick={resetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    height: 28,
                    px: 0.8,
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    color: '#dc2626',
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    textTransform: 'none',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#fee2e2' },
                  }}
                >
                  Restablecer
                </Button>
              )}
            </Box>
          </Box>

          {/* Vista Laptop / Tablet (md a xl): Barra compacta en 1 sola fila */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex', xl: 'none' },
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              minWidth: 0,
              width: '100%',
              bgcolor: '#ffffff',
              p: 0.4,
              px: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                bgcolor: '#ffffff',
                px: 0.8,
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                height: 26,
                flexShrink: 0,
                '&:hover': { borderColor: '#94a3b8' },
              }}
            >
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(tempFilterData, quickSearch); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: '0.66rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                    '&::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                  },
                }}
              />
              <Box sx={{ width: '1px', height: 14, bgcolor: '#cbd5e1', mx: 0.3 }} />
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(tempFilterData, quickSearch); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: '0.66rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                    '&::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                  },
                }}
              />
            </Box>

            {/* 2. Menú Desplegable de Estatus */}
            <TableFilterChips
              items={statusChipsMemo}
              activeFilter={tempFilterData.ESTATUS || ''}
              onFilterClick={handleStatusFilterClick}
              disabled={loadingData}
              isCompact={true}
            />

            {/* 3. Selector Jerárquico Estilo Breadcrumbs: Empresa / Proyecto / Grupo / CC */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setHierarchicalAnchorEl(e.currentTarget);
              }}
              startIcon={<AccountTreeIcon sx={{ fontSize: '13px !important', color: hasHierarchicalFilter ? '#0655A5' : '#64748b' }} />}
              endIcon={hierarchicalAnchorEl ? <ExpandLessIcon sx={{ fontSize: 13 }} /> : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
              sx={{
                height: 26,
                px: 0.8,
                minWidth: 70,
                maxWidth: { sm: 130, md: 155, lg: 185 },
                fontSize: '0.68rem',
                fontWeight: hasHierarchicalFilter ? 700 : 500,
                borderRadius: '10px',
                bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                color: hasHierarchicalFilter ? '#1e40af' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc', borderColor: '#60a5fa' },
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {hierarchicalSummary}
              </Box>
            </Button>

            {/* 4. Botón Más Filtros (Slim) */}
            <Button
              size="small"
              variant={showFiltersPanel ? 'contained' : 'outlined'}
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(prev => !prev);
              }}
              startIcon={<FilterListIcon sx={{ fontSize: '13px !important' }} />}
              endIcon={showFiltersPanel ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 26,
                px: 0.6,
                fontSize: '0.68rem',
                fontWeight: 600,
                borderRadius: '10px',
                bgcolor: showFiltersPanel ? '#0f172a' : '#ffffff',
                borderColor: '#cbd5e1',
                color: showFiltersPanel ? '#ffffff' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Más {extraFiltersCount > 0 ? `(${extraFiltersCount})` : ''}
            </Button>

            {/* 5. Buscador */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 13 }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                minWidth: 75,
                width: { md: 110, lg: 125 },
                flex: '0 1 125px',
                flexShrink: 1,
                '& .MuiOutlinedInput-root': {
                  height: 26,
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                }
              }}
            />

          </Box>

          {/* Vista Escritorio (xl en adelante): FILTROS A LA IZQUIERDA -> DIVISOR -> BUSCADOR GENERAL A LA DERECHA */}
          <Box
            sx={{
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 'clamp(4px, 0.5vw, 7px)',
              bgcolor: '#ffffff',
              p: 'clamp(3px, 0.4vw, 5px)',
              px: 'clamp(6px, 0.8vw, 12px)',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
              flex: 1,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: '#ffffff', px: 0.8, py: 0.1, borderRadius: '10px', border: '1px solid #cbd5e1', height: 'clamp(26px, 2vw, 28px)', flexShrink: 0, '&:hover': { borderColor: '#94a3b8' } }}>
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(tempFilterData, quickSearch); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                  },
                }}
              />
              <Box sx={{ width: '1px', height: 14, bgcolor: '#cbd5e1', mx: 0.3 }} />
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(tempFilterData, quickSearch); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                  },
                }}
              />
            </Box>

            {/* 2. Select Empresa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={empresaOptions}
              value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  EMPRESA_IDS: value.map(empresa => empresa.id),
                  EMPRESA: value.map(empresa => empresa.nombre).join(','),
                  PROYECTOS: [],
                  DIVISIONES: [],
                  CENTRO_COSTOS: [],
                };
                setTempFilterData(updated);
                setFilterData(updated);
                setTableParams(prev => ({ ...prev, page: 1 }));
              }}
              getOptionLabel={(option) => option.nombre}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Empresa', empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre))
              )}
              sx={{ width: 'clamp(100px, 8.5vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 2.1 Select Proyecto */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proyectoOptions}
              value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  PROYECTOS: value.map(p => p.id),
                  PROYECTO: value.map(p => p.nombre).join(','),
                  DIVISIONES: [],
                  CENTRO_COSTOS: [],
                };
                setTempFilterData(updated);
              }}
              getOptionLabel={(option) => option.nombre}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Proyecto', proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre))
              )}
              sx={{ width: 'clamp(95px, 8vw, 118px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 2.2 Select Grupo / División */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisionOptions}
              value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  DIVISIONES: value.map(d => d.id),
                  DIVISION: value.map(d => d.nombre).join(','),
                  CENTRO_COSTOS: [],
                };
                setTempFilterData(updated);
              }}
              getOptionLabel={(option) => option.nombre}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Grupo / Div', divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre))
              )}
              sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 3. Select Centro Costo */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={centroCostoOptions}
              value={tempFilterData.CENTRO_COSTOS || []}
              onChange={(_, value) => {
                updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false });
              }}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])
              )}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 4. Select Depto */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={departamentoOptions}
              value={tempFilterData.DEPARTAMENTOS || []}
              onChange={(_, value) => {
                updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false });
              }}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Depto', tempFilterData.DEPARTAMENTOS || [])
              )}
              sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 5. Select Proveedor */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proveedorOptions}
              value={tempFilterData.PROVEEDORES || []}
              onChange={(_, value) => {
                updateVisibleFilter('PROVEEDORES', value || [], { apply: false });
              }}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])
              )}
              sx={{ width: 'clamp(105px, 9.5vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 6. Select Divisa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisaOptions}
              value={tempFilterData.DIVISAS || []}
              onChange={(_, value) => {
                updateVisibleFilter('DIVISAS', value || [], { apply: false });
              }}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])
              )}
              sx={{ width: 'clamp(75px, 6vw, 90px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 7. Select Compradora */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={compradoraOptions}
              value={tempFilterData.COMPRADORAS || []}
              onChange={(_, value) => {
                updateVisibleFilter('COMPRADORAS', value || [], { apply: false });
              }}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(params, 'Comprador', tempFilterData.COMPRADORAS || [])
              )}
              sx={{ width: 'clamp(95px, 8vw, 118px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* Separador vertical */}
            <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 20, mx: 0.3, flexShrink: 0 }} />

            {/* 8. Buscador General al final */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, quickSearch);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 'clamp(12px, 0.9vw, 14px)' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 'clamp(10px, 0.8vw, 12px)' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: 'clamp(100px, 8vw, 130px)',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 'clamp(26px, 2vw, 28px)',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
                }
              }}
            />

            {/* 9. Botón Buscar */}
            <Button
              size="small"
              variant="contained"
              disabled={loadingData}
              onClick={() => applyFilters(tempFilterData, quickSearch)}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(8px, 0.8vw, 12px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 700,
                borderRadius: '10px',
                bgcolor: '#0655A5',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#054785' },
              }}
            >
              Buscar
            </Button>

            {/* 10. Botón Limpiar */}
            <Button
              size="small"
              variant="outlined"
              disabled={loadingData}
              onClick={resetFilters}
              startIcon={<ClearIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(6px, 0.6vw, 10px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Box>

        {/* Acciones a la derecha en Desktop y Laptop: Sincronizar -> KPIs */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
          <SyncDialog
            onSync={handleImport}
            defaultFechaInicio={tempFilterData.FECHA_INICIO}
            defaultFechaFin={tempFilterData.FECHA_FIN}
            defaultFolio={tempFilterData.FOLIO}
            isCompact={true}
          />

          <Tooltip title={showAnalyticsCards ? 'Ocultar Tarjetas Analíticas' : 'Ver Tarjetas Analíticas'}>
            <Button
              size="small"
              variant={showAnalyticsCards ? 'contained' : 'outlined'}
              onClick={() => setShowAnalyticsCards(prev => !prev)}
              startIcon={<AnalyticsIcon sx={{ fontSize: '13px !important' }} />}
              endIcon={showAnalyticsCards ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: { xs: 24, sm: 26 },
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '14px',
                bgcolor: showAnalyticsCards ? '#0284c7' : '#ffffff',
                borderColor: showAnalyticsCards ? '#0284c7' : '#cbd5e1',
                color: showAnalyticsCards ? '#fff' : '#0284c7',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: showAnalyticsCards ? '#0369a1' : '#f0f9ff',
                  borderColor: '#0284c7',
                }
              }}
            >
              KPIs
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Popover 1: Selector Jerárquico Integrado (Empresa > Proyecto > Grupo > Centro de Costo) */}
      <Popover
        open={Boolean(hierarchicalAnchorEl)}
        anchorEl={hierarchicalAnchorEl}
        onClose={() => setHierarchicalAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: { xs: 1.8, sm: 2.2 },
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0 12px 36px -5px rgba(0,0,0,0.2)',
            border: '1px solid #cbd5e1',
            width: { xs: 'calc(100vw - 28px)', sm: 340 },
            maxWidth: '380px',
            maxHeight: { xs: '80vh', sm: '85vh' },
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.6,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0.5, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <AccountTreeIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Jerarquía Organizacional
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHierarchicalAnchorEl(null)} sx={{ p: 0.2 }}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Empresa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            1. EMPRESA
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                EMPRESA_IDS: value.map(empresa => empresa.id),
                EMPRESA: value.map(empresa => empresa.nombre).join(','),
                PROYECTOS: [],
                DIVISIONES: [],
                CENTRO_COSTOS: [],
              };
              setTempFilterData(updated);
              setFilterData(updated);
              setTableParams(prev => ({ ...prev, page: 1 }));
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Empresa', empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre))}
            fullWidth
          />
        </Box>

        {/* 2. Proyecto */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            2. PROYECTO
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                PROYECTOS: value.map(p => p.id),
                PROYECTO: value.map(p => p.nombre).join(','),
                DIVISIONES: [],
                CENTRO_COSTOS: [],
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proyecto', proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre))}
            fullWidth
          />
        </Box>

        {/* 3. Grupo / División */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            3. GRUPO / DIVISIÓN
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                DIVISIONES: value.map(d => d.id),
                DIVISION: value.map(d => d.nombre).join(','),
                CENTRO_COSTOS: [],
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Grupo / División', divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre))}
            fullWidth
          />
        </Box>

        {/* 4. Centro de Costo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            4. CENTRO DE COSTO
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={centroCostoOptions}
            value={tempFilterData.CENTRO_COSTOS || []}
            onChange={(_, value) => updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false })}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
            fullWidth
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const cleared = {
                ...tempFilterData,
                EMPRESA_IDS: [],
                EMPRESA: '',
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(cleared);
              setFilterData(cleared);
              setHierarchicalAnchorEl(null);
              aplicarFiltrosExternos(cleared, quickSearch);
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar Jerarquía
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters();
              setHierarchicalAnchorEl(null);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>

      {/* Popover 2: Filtros Complementarios / Avanzados */}
      <Popover
        open={showFiltersPanel}
        anchorEl={filterAnchorEl}
        onClose={() => setShowFiltersPanel(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: { xs: 1.8, sm: 2 },
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0 12px 36px -5px rgba(0,0,0,0.2)',
            border: '1px solid #cbd5e1',
            width: { xs: 'calc(100vw - 28px)', sm: 340 },
            maxWidth: '380px',
            maxHeight: { xs: '80vh', sm: '85vh' },
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
          Otros Filtros
        </Typography>

        <Autocomplete
          disabled={loadingData}
          multiple
          freeSolo
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={departamentoOptions}
          value={tempFilterData.DEPARTAMENTOS || []}
          onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
          fullWidth
        />

        <Autocomplete
          disabled={loadingData}
          multiple
          freeSolo
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={proveedorOptions}
          value={tempFilterData.PROVEEDORES || []}
          onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
          fullWidth
        />

        <Autocomplete
          disabled={loadingData}
          multiple
          freeSolo
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={divisaOptions}
          value={tempFilterData.DIVISAS || []}
          onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
          fullWidth
        />

        <Autocomplete
          disabled={loadingData}
          multiple
          freeSolo
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={compradoraOptions}
          value={tempFilterData.COMPRADORAS || []}
          onChange={(_, value) => updateVisibleFilter('COMPRADORAS', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Comprador', tempFilterData.COMPRADORAS || [])}
          fullWidth
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setTempFilterData(baseFilters);
              setFilterData(baseFilters);
              setQuickSearch('');
              setShowFiltersPanel(false);
              aplicarFiltrosExternos(baseFilters, '');
            }}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Limpiar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters();
              setShowFiltersPanel(false);
            }}
            sx={{ fontSize: 12, textTransform: 'none', bgcolor: '#0655A5', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>

      {/* ── PANEL DE TARJETAS BENTO ANALYTICS (RESPONSIVE FLUID RIBBON) ── */}
      <Collapse in={showAnalyticsCards} unmountOnExit>
        <Box sx={{ mb: 1, px: 0.2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
              gap: 0.8,
              alignItems: 'stretch'
            }}
          >
            {/* ── TARJETA 1: VOLUMEN TOTAL Y DESGLOSE POR DIVISAS ── */}
            <Paper
              elevation={0}
              sx={{
                p: '5px 8px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                overflow: 'hidden',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              {/* Encabezado con Volumen y Total Aprox MXN */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  pr: { xs: 0, sm: 0.8 },
                  borderRight: { xs: 'none', sm: '1px solid #f1f5f9' },
                  borderBottom: { xs: '1px solid #f1f5f9', sm: 'none' },
                  pb: { xs: 0.5, sm: 0 },
                  flexShrink: 0,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <Box sx={{ p: 0.4, bgcolor: '#eff6ff', borderRadius: '6px', color: '#0284c7', display: 'flex' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
                      Volumen
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, mt: 0.1 }}>
                      {kpisSummary.totalCount.toLocaleString()} <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#64748b' }}>OCs</span>
                    </Typography>
                  </Box>
                </Box>

                {/* Total Estimado en Pesos (MXN) */}
                <Tooltip title={`Total global de las ${kpisSummary.totalCount} OCs del periodo (MXN + conversión aprox USD $18.50 y EUR $20.00)`} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      bgcolor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      px: 0.6,
                      py: 0.15,
                      borderRadius: '6px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', lineHeight: 1 }}>
                      Total Aprox.
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 900, color: '#166534', lineHeight: 1.1, mt: 0.1 }}>
                      ${kpisSummary.totalEstimadoMXN.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span style={{ fontSize: '0.52rem', fontWeight: 700 }}>MXN</span>
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>

              {/* Desglose por Divisas */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.6,
                  flex: 1,
                  alignItems: 'center',
                  minWidth: 0,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  width: { xs: '100%', sm: 'auto' },
                  pt: { xs: 0.4, sm: 0 },
                }}
              >
                {Object.keys(kpisSummary.divisas).length > 0 ? (
                  Object.entries(kpisSummary.divisas).map(([divisa, dvals]) => {
                    const colorBadge = divisa === 'USD' ? '#d97706' : divisa === 'EUR' ? '#4f46e5' : '#0284c7';
                    const bgBadge = divisa === 'USD' ? '#fffbeb' : divisa === 'EUR' ? '#eef2ff' : '#f0f9ff';
                    const borderBadge = divisa === 'USD' ? '#fde68a' : divisa === 'EUR' ? '#c7d2fe' : '#bae6fd';

                    return (
                      <Box
                        key={divisa}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: 0.1,
                          px: 0.6,
                          py: 0.2,
                          borderRadius: '6px',
                          bgcolor: bgBadge,
                          border: `1px solid ${borderBadge}`,
                          flex: { xs: '0 0 auto', sm: '1 1 0' },
                          minWidth: { xs: 110, sm: 0 },
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.4, flexShrink: 0 }}>
                          <Chip
                            label={divisa}
                            size="small"
                            sx={{
                              fontWeight: 900,
                              fontSize: '0.52rem',
                              height: 15,
                              bgcolor: '#ffffff',
                              color: colorBadge,
                              border: `1px solid ${borderBadge}`,
                              px: 0.1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: '0.52rem', color: '#64748b', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {dvals.count} OC{dvals.count > 1 ? 's' : ''}
                          </Typography>
                        </Box>

                        <Typography
                          title={`$${dvals.total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          sx={{
                            fontSize: 'clamp(0.60rem, 0.54rem + 0.12vw, 0.72rem)',
                            fontWeight: 900,
                            color: '#0f172a',
                            lineHeight: 1.1,
                            mt: 0.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          ${dvals.total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    Sin datos en el periodo
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* ── TARJETA 2: EMBUDOS OPERATIVOS ── */}
            <Paper
              elevation={0}
              sx={{
                p: '5px 8px',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                overflow: 'hidden',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              {/* Encabezado */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  pr: { xs: 0, sm: 0.8 },
                  borderRight: { xs: 'none', sm: '1px solid #f1f5f9' },
                  borderBottom: { xs: '1px solid #f1f5f9', sm: 'none' },
                  pb: { xs: 0.5, sm: 0 },
                  flexShrink: 0,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{ p: 0.4, bgcolor: '#fef3c7', borderRadius: '6px', color: '#d97706', display: 'flex' }}>
                    <PendingActionsIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: '0.60rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1 }}>
                      Embudos
                    </Typography>
                    <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, mt: 0.1, whiteSpace: 'nowrap' }}>
                      Trámite <span style={{ fontSize: '0.60rem', fontWeight: 600, color: '#64748b' }}>Pago</span>
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Flujo"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.52rem',
                    fontWeight: 800,
                    bgcolor: '#fffbeb',
                    color: '#b45309',
                    display: { xs: 'none', md: 'inline-flex' }
                  }}
                />
              </Box>

              {/* 3 Bloques Operativos */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.6,
                  flex: 1,
                  alignItems: 'center',
                  minWidth: 0,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  width: { xs: '100%', sm: 'auto' },
                  pt: { xs: 0.4, sm: 0 },
                }}
              >
                {/* 1. Sin Solicitud */}
                <Box
                  title={`Sin Solicitud: $${kpisSummary.funnel.sinSolicitud.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  sx={{
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '6px',
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    flex: { xs: '0 0 auto', sm: '1 1 0' },
                    minWidth: { xs: 105, sm: 0 },
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.3, mb: 0.1 }}>
                    <Typography sx={{ fontSize: '0.50rem', fontWeight: 700, color: '#64748b', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      Sin Sol.
                    </Typography>
                    <Chip
                      label={`${kpisSummary.funnel.sinSolicitud.count}`}
                      size="small"
                      sx={{ height: 15, fontSize: '0.50rem', fontWeight: 800, bgcolor: '#e2e8f0', color: '#334155', px: 0.1, flexShrink: 0 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.52rem + 0.12vw, 0.70rem)', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ${kpisSummary.funnel.sinSolicitud.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {/* 2. Solicitud Parcial */}
                <Box
                  title={`Parcial: $${kpisSummary.funnel.solParcial.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  sx={{
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '6px',
                    bgcolor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    flex: { xs: '0 0 auto', sm: '1 1 0' },
                    minWidth: { xs: 105, sm: 0 },
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.3, mb: 0.1 }}>
                    <Typography sx={{ fontSize: '0.50rem', fontWeight: 700, color: '#1d4ed8', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      Parcial
                    </Typography>
                    <Chip
                      label={`${kpisSummary.funnel.solParcial.count}`}
                      size="small"
                      sx={{ height: 15, fontSize: '0.50rem', fontWeight: 800, bgcolor: '#dbeafe', color: '#1e40af', px: 0.1, flexShrink: 0 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.52rem + 0.12vw, 0.70rem)', fontWeight: 900, color: '#1e40af', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ${kpisSummary.funnel.solParcial.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {/* 3. Solicitud Total */}
                <Box
                  title={`Total: $${kpisSummary.funnel.solTotal.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  sx={{
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '6px',
                    bgcolor: '#faf5ff',
                    border: '1px solid #e9d5ff',
                    flex: { xs: '0 0 auto', sm: '1 1 0' },
                    minWidth: { xs: 105, sm: 0 },
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.3, mb: 0.1 }}>
                    <Typography sx={{ fontSize: '0.50rem', fontWeight: 700, color: '#7e22ce', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      Total
                    </Typography>
                    <Chip
                      label={`${kpisSummary.funnel.solTotal.count}`}
                      size="small"
                      sx={{ height: 15, fontSize: '0.50rem', fontWeight: 800, bgcolor: '#f3e8ff', color: '#6b21a8', px: 0.1, flexShrink: 0 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.52rem + 0.12vw, 0.70rem)', fontWeight: 900, color: '#6b21a8', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ${kpisSummary.funnel.solTotal.monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          loading={loadingData}
          buttons={buttons}
          dataTable={dataForTable}
          serverPagination={true}
          totalServerRows={data?.total || 0}
          onServerPaginationChange={({ page, pageSize }) => {
            setTableParams(prev => ({ ...prev, page, pageSize }));
          }}
          onStateChange={handleTableStateChange}
          showExportButton={true}
          enableSorting={false}
          enableFiltering={false}
          enableGlobalSearch={false}
          hiddenFields={tableHiddenFieldsMemo}
          defaultHiddenCols={defaultHiddenCols}
          columnLabels={tableColumnLabels}
          initialColumnOrder={compactColumnOrder}
          density="small"
          compactActions
          maxInlineActions={1}
          stickyLeftKeys={[]}
          stickyLeftWidths={{ __expand__: 36, OC_REQUI: 160 }}
          onRowClick={handleOpenSolicitudes}
          leftButtons={
            <Box sx={{ display: { xs: 'none', xl: 'block' }, opacity: loadingData ? 0.6 : 1, pointerEvents: loadingData ? 'none' : 'auto' }}>
              <TableFilterChips
                title="Filtrar estatus:"
                activeFilter={filterData.ESTATUS || ''}
                onFilterClick={handleStatusChipFilter}
                items={ordenCompraStatusChips}
              />
            </Box>
          }
          rightButtons={<ExportButton data={data || []} onFetchAll={handleExportFetchAll} hidePendientes={true} tableLoading={loadingData} />}
          mobileCard={{ statusMode: 'none' }}
          persistEnabled
          persistKey="Solicitudes"
          persistVista="layoutCompactResponsiveV3"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
          emptyStateData={emptyStateData}
          renderFooterRow={renderFooterRowMemo}
          renderMobileFooter={renderMobileFooterMemo}
        />
      </Box>

      {/* ── POPOVER AISLADO: su estado interno NO re-renderiza CollapsibleTable ── */}
      <TotalesPopover
        ref={totalesPopoverRef}
        divisasRef={kpisDivisasRef}
        funnelRef={kpisFunnelRef}
      />

      <ModalSolicitudes
        open={modalSolicitudesOpen}
        onClose={handleCloseSolicitudes}
        onSaved={handleSolicitudGuardada}
        rowData={selectedRow}
      />

      <SimpleSuccessSpinner
        open={loadingSpinner}
        handleClose={() => setLoadingSpinner(false)}
        message={spinnerMessage}
      />
    </Box>
  );
}
